// packages/front/src/portfolio/modals/close-account-modal.tsx
//
// Close an empty account on a chosen date. Closing keeps the whole history and every
// calculation; the server refuses with 409 while money, securities or later records remain.

import { useEffect, useState, type FC, type FormEvent } from "react";
import { utcDateToCalendarDate } from "@signals/shared";
import { Button, Modal } from "../../_common";
import { useClosePortfolioAccount, type Account } from "../portfolio.hooks";
import { apiErrorMessage } from "../portfolio-api-error";

export interface CloseAccountModalProps {
  portfolioId: string;
  /** Account to close; the modal is open while it is set. */
  account: Account | null;
  /** Initial closure date, normally the date selected on the page. */
  defaultDate: string;
  onClose: () => void;
  onClosed?: (account: Account) => void;
}

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

export const CloseAccountModal: FC<CloseAccountModalProps> = ({
  portfolioId,
  account,
  defaultDate,
  onClose,
  onClosed,
}) => {
  const closeAccount = useClosePortfolioAccount(portfolioId);
  const today = utcDateToCalendarDate(new Date());
  const [closedOn, setClosedOn] = useState(defaultDate);
  const [closureNote, setClosureNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const accountId = account?.id;
  useEffect(() => {
    if (!accountId) return;
    setClosedOn(defaultDate > today ? today : defaultDate);
    setClosureNote("");
    setError(null);
    // The form is re-seeded only when another account is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!account) return;
    if (!closedOn) {
      setError("Укажите дату закрытия");
      return;
    }
    if (!closureNote.trim()) {
      setError("Укажите основание закрытия");
      return;
    }
    setError(null);
    try {
      const closed = await closeAccount.mutateAsync({
        id: account.id,
        body: { closedOn, closureNote: closureNote.trim() },
      });
      onClosed?.(closed);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Не удалось закрыть счёт"));
    }
  };

  return (
    <Modal open={account !== null} onClose={onClose} title="Закрыть счёт" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <p className="text-sm">
          Счёт <span className="font-medium">{account?.name}</span> будет закрыт включительно с выбранной даты.
          История, доходность и сохранённые снимки остаются без изменений; новые операции позднее этой даты
          будут запрещены.
        </p>
        <p className="text-xs text-muted-foreground">
          Закрыть можно только пустой счёт: на конец этой даты не должно быть денег ни в одной валюте, бумаг
          и незавершённых конвертаций, а позднее — никаких операций.
        </p>
        {error && (
          <p role="alert" className="whitespace-pre-wrap break-words text-sm text-destructive">
            {error}
          </p>
        )}
        <label className={labelClass} htmlFor="close-account-date">Дата закрытия</label>
        <input
          id="close-account-date"
          className={inputClass}
          type="date"
          max={today}
          value={closedOn}
          onChange={(e) => {
            setClosedOn(e.target.value);
            setError(null);
          }}
          required
        />
        <label className={labelClass} htmlFor="close-account-note">Основание</label>
        <input
          id="close-account-note"
          className={inputClass}
          value={closureNote}
          onChange={(e) => {
            setClosureNote(e.target.value);
            setError(null);
          }}
          placeholder="Например: техническая дата учёта после сверки остатков"
          required
        />
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={closeAccount.isPending}>
            {closeAccount.isPending ? "Закрываем..." : "Закрыть счёт"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
